import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import { Check, Sparkles, Crown, Rocket, Tag, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";



export default function PackageSelection() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      console.log("Fetching packages from:", `${API}/packages`);
      const response = await axios.get(`${API}/packages`);
      console.log("Packages response:", response.data);
      setPackages(response.data);
    } catch (error) {
      console.error("Error fetching packages:", error);
      console.error("Error response:", error.response?.data);
      toast.error("Fehler beim Laden der Pakete");
    }
  };

  // Validate and apply coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error("Bitte gib einen Gutscheincode ein");
      return;
    }

    setCouponLoading(true);
    try {
      const response = await axios.post(`${API}/api/coupons/validate`, {
        code: couponCode.trim()
      }, { withCredentials: true });

      if (response.data.valid) {
        setAppliedCoupon(response.data);
        toast.success(`Gutschein "${response.data.name}" angewendet!`);
      } else {
        toast.error(response.data.message || "Ungültiger Gutscheincode");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Fehler bei der Gutscheinvalidierung";
      toast.error(errorMessage);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    toast.info("Gutschein entfernt");
  };

  // Calculate discounted price
  const getDiscountedPrice = (originalPrice) => {
    if (!appliedCoupon || originalPrice === 0) return originalPrice;

    if (appliedCoupon.discount_type === "percent") {
      return originalPrice * (1 - appliedCoupon.discount_value / 100);
    } else if (appliedCoupon.discount_type === "fixed") {
      return Math.max(0, originalPrice - appliedCoupon.discount_value);
    } else if (appliedCoupon.discount_type === "free") {
      return 0;
    }
    return originalPrice;
  };

  const handleSelectPackage = async (pkg) => {
    setLoading(true);
    setSelectedPackage(pkg.id);

    try {
      if (pkg.tier === "free") {
        // FREE Paket - direkt aktivieren
        const response = await axios.post(
          `${API}/user/select-package`,
          { package_id: pkg.id },
          { withCredentials: true }
        );
        toast.success("FREE Paket aktiviert!");

        // Refresh auth state before navigating to dashboard
        if (window.checkAuth) {
          await window.checkAuth();
        }

        navigate("/dashboard");
      } else {
        // PREMIUM/PRO - zu Stripe Checkout
        const requestData = { package_id: pkg.id };

        // Include coupon if applied
        if (appliedCoupon) {
          requestData.coupon_code = appliedCoupon.code;
        }

        const response = await axios.post(
          `${API}/stripe/create-checkout`,
          requestData,
          { withCredentials: true }
        );

        if (response.data.url) {
          window.location.href = response.data.url;
        } else {
          toast.error("Keine Checkout-URL erhalten");
        }
      }
    } catch (error) {
      console.error("Package selection error:", error);

      // Extract error message properly
      let errorMessage = "Fehler bei der Paketauswahl";

      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else if (Array.isArray(error.response.data.detail)) {
            errorMessage = error.response.data.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
          }
        }
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setSelectedPackage(null);
    }
  };

  const getPackageIcon = (tier) => {
    switch (tier) {
      case "free": return <Sparkles className="w-8 h-8" />;
      case "premium": return <Crown className="w-8 h-8" />;
      case "pro": return <Rocket className="w-8 h-8" />;
      default: return <Sparkles className="w-8 h-8" />;
    }
  };

  const getPackageColor = (tier) => {
    switch (tier) {
      case "free": return "from-blue-500/20 to-cyan-500/20";
      case "premium": return "from-purple-500/20 to-pink-500/20";
      case "pro": return "from-orange-500/20 to-red-500/20";
      default: return "from-gray-500/20 to-gray-600/20";
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="max-w-6xl w-full">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-serif text-4xl md:text-5xl text-foreground mb-4">
            Wähle dein Paket
          </h1>
          <p className="text-muted-foreground text-lg">
            Starte kostenlos oder wähle Premium für mehr Möglichkeiten
          </p>
        </div>

        {/* Coupon Input Section */}
        <div className="max-w-md mx-auto mb-8">
          <div className="bg-card rounded-2xl p-4 border border-border/50 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="w-5 h-5 text-primary" />
              <span className="font-medium text-foreground">Gutscheincode</span>
            </div>

            {appliedCoupon ? (
              <div className="flex items-center justify-between bg-[#001D24]/10 border border-[#001D24]/30 rounded-xl p-3">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-[#001D24]" />
                  <div>
                    <p className="font-medium text-[#001D24]">{appliedCoupon.name}</p>
                    <p className="text-sm text-[#001D24]/80">
                      {appliedCoupon.discount_type === "percent"
                        ? `${appliedCoupon.discount_value}% Rabatt`
                        : appliedCoupon.discount_type === "fixed"
                          ? `${appliedCoupon.discount_value}€ Rabatt`
                          : "Kostenlos"}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={removeCoupon}
                  className="h-8 w-8 text-[#001D24] hover:text-red-600 hover:bg-red-50"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  placeholder="Code eingeben..."
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
                  className="flex-1 uppercase"
                  disabled={couponLoading}
                />
                <Button
                  onClick={handleApplyCoupon}
                  disabled={couponLoading || !couponCode.trim()}
                  variant="outline"
                  className="px-4"
                >
                  {couponLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Anwenden"
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Package Cards */}
        <div className={`grid grid-cols-1 ${packages.length === 4 ? 'md:grid-cols-4' : packages.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-8 items-stretch`}>
          {packages.map((pkg, index) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`flex flex-col h-full ${pkg.tier === "premium"
                ? "relative bg-primary text-primary-foreground rounded-3xl p-8 border-2 border-primary shadow-2xl transform scale-105"
                : "bg-card rounded-3xl p-8 border border-border/50 shadow-lg"
                }`}
            >
              {pkg.tier === "premium" && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-accent text-accent-foreground px-4 py-1 rounded-full text-sm font-medium">
                  🔥 Beliebt
                </div>
              )}

              {/* Icon */}
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${getPackageColor(pkg.tier)} flex items-center justify-center mb-6 ${pkg.tier === 'premium' ? 'text-primary-foreground' : 'text-primary'}`}>
                {getPackageIcon(pkg.tier)}
              </div>

              {/* Package Name */}
              <h3 className={`text-2xl font-bold mb-2 ${pkg.tier === 'premium' ? '' : 'text-foreground'}`}>
                {pkg.name}
              </h3>

              {/* Description List - Fixed Height */}
              <div className="h-16 mb-4">
                {(pkg.description_list && pkg.description_list.length > 0) ? (
                  <div className="space-y-1">
                    {pkg.description_list.slice(0, 3).map((item, idx) => (
                      <p key={idx} className={`text-sm ${pkg.tier === 'premium' ? 'opacity-90' : 'text-muted-foreground'}`}>
                        • {item}
                      </p>
                    ))}
                  </div>
                ) : pkg.description ? (
                  <p className={`text-sm ${pkg.tier === 'premium' ? 'opacity-90' : 'text-muted-foreground'}`}>
                    {pkg.description}
                  </p>
                ) : null}
              </div>

              {/* Price - Fixed Height */}
              <div className="h-16 mb-6 flex flex-col justify-center">
                {pkg.price === 0 ? (
                  <div className={`text-4xl font-bold ${pkg.tier === 'premium' ? '' : 'text-foreground'}`}>
                    Kostenlos
                  </div>
                ) : (
                  <div className="flex items-baseline gap-2 flex-wrap">
                    {appliedCoupon && getDiscountedPrice(pkg.price) !== pkg.price ? (
                      <>
                        <span className={`text-4xl font-bold ${pkg.tier === 'premium' ? '' : 'text-foreground'}`}>
                          €{getDiscountedPrice(pkg.price).toFixed(2)}
                        </span>
                        <span className={`text-xl line-through opacity-50 ${pkg.tier === 'premium' ? '' : 'text-muted-foreground'}`}>
                          €{pkg.price}
                        </span>
                      </>
                    ) : (
                      <span className={`text-4xl font-bold ${pkg.tier === 'premium' ? '' : 'text-foreground'}`}>
                        €{Math.round(pkg.price)}
                      </span>
                    )}
                    {pkg.is_subscription && (
                      <span className={`text-sm ${pkg.tier === 'premium' ? 'opacity-80' : 'text-muted-foreground'}`}>
                        /Monat
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Features - Flexible, grows to fill space */}
              <ul className="space-y-3 mb-8 flex-1">
                {(pkg.feature_list || []).slice(0, 6).map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${pkg.tier === 'premium' ? '' : 'text-[#001D24]'}`} />
                    <span className={`text-sm ${pkg.tier === 'premium' ? '' : 'text-foreground'}`}>
                      {feature}
                    </span>
                  </li>
                ))}
                {(pkg.feature_list || []).length > 6 && (
                  <li className={`text-xs text-center ${pkg.tier === 'premium' ? 'opacity-80' : 'text-muted-foreground'}`}>
                    + {(pkg.feature_list || []).length - 6} weitere Features
                  </li>
                )}
              </ul>

              {/* CTA Button - Always at bottom */}
              <Button
                onClick={() => handleSelectPackage(pkg)}
                disabled={loading && selectedPackage === pkg.id}
                variant={pkg.tier === "premium" ? "secondary" : "default"}
                className="w-full py-6 rounded-full text-base font-medium"
              >
                {loading && selectedPackage === pkg.id ? (
                  "Wird geladen..."
                ) : pkg.price === 0 ? (
                  "Kostenlos starten"
                ) : (
                  `${pkg.name} wählen`
                )}
              </Button>

              {pkg.tier === "free" && (
                <p className="text-xs text-center mt-4 opacity-70">
                  Keine Kreditkarte erforderlich
                </p>
              )}
            </motion.div>
          ))}
        </div>

        {/* Info Text */}
        <div className="text-center mt-12 text-sm text-muted-foreground">
          <p>Alle Pakete beinhalten: RSVP-System, Gästebuch, Standort-Karte, Chat</p>
          <p className="mt-2">Du kannst jederzeit upgraden oder downgraden</p>
        </div>


      </div>
    </div>
  );
}
